import type { BackgroundPreset, LocaleCode } from "@/shared/types/surface";
import type { GeometryPresetKey } from "@/shared/types/math";
import type { SubjectCategoryId } from "@/data/educationalLibraries";
import type {
  LatexChipId,
  LatexColorId,
} from "@/components/whiteboard/latex/latexConstants";
import { VI_TRANSLATIONS } from "./vi";
import { ZH_CN_TRANSLATIONS } from "./zhCN";
import { ZH_TW_TRANSLATIONS } from "./zhTW";
import { EN_TRANSLATIONS } from "./en";

export type HudShapeToolKey = "rectangle" | "diamond" | "ellipse" | "arrow" | "line";

export interface StudioCardTranslation {
  readonly title: string;
  readonly subtitle: string;
  readonly description: string;
  readonly tags: readonly string[];
}

export interface WhiteboardTranslations {
  readonly workspace: {
    readonly cancelLabel: string;
    readonly syncingSession: string;
    readonly initializingBoard: string;
    readonly clearSlideModalTitle: string;
    readonly clearSlideModalDescription: string;
    readonly clearSlideConfirmLabel: string;
    readonly slideClearedToastTitle: string;
    readonly slideClearedToastDesc: string;
    readonly slowNetworkAria: string;
    readonly slowNetworkTitle: string;
    readonly slowNetworkDesc: string;
    readonly retrySyncButton: string;
    readonly toolErrorTitle: string;
    readonly toolErrorFallback: string;
    readonly toolReloadButton: string;
    readonly dismissToastAria: string;
  };
  readonly hud: {
    readonly appMenuAriaLabel: string;
    readonly boardMenuAriaLabel: string;
    readonly exportPng: string;
    readonly exportPdf: string;
    readonly pdfScopeAriaLabel: string;
    readonly pdfScopeAll: string;
    readonly pdfScopeTab: string;
    readonly pdfScopeSlide: string;
    readonly findOnCanvas: string;
    readonly help: string;
    readonly clearSlide: string;
    readonly paperStyleSectionTitle: string;
    readonly strokeSmoothingSectionTitle: string;
    readonly canvasBgSectionTitle: string;
    readonly canvasBgSwatchAria: (color: string) => string;
    readonly customBgColorAria: string;
    readonly paperPresets: Readonly<Record<BackgroundPreset, string>>;
    readonly prevSlideAria: string;
    readonly nextSlideAria: string;
    readonly addSlideAria: string;
    readonly duplicateSlideAria: string;
    readonly duplicateSlideTitle: string;
    readonly deleteSlideAria: string;
    readonly confirmDeleteSlideAria: string;
    readonly confirmDeleteSlideLabel: string;
    readonly cancelDeleteSlideAria: string;
    readonly cancelLabel: string;
    readonly drawingToolbarAria: string;
    readonly pointerSelectionLabel: string;
    readonly pointerHandLabel: string;
    readonly shapesMenuAria: string;
    readonly shapeTools: Readonly<Record<HudShapeToolKey, string>>;
    readonly freedrawAria: string;
    readonly freedrawTitle: string;
    readonly textAria: string;
    readonly textTitle: string;
    readonly eraserAria: string;
    readonly eraserTitle: string;
    readonly insertMenuTriggerAria: string;
    readonly insertMenuTriggerTitle: string;
    readonly insertPopoverAria: string;
    readonly insertImageLabel: string;
    readonly pinyinAssistantTrigger: string;
    readonly stemTriggerAria: string;
    readonly stemTriggerTitle: string;
    readonly stemPopoverAria: string;
    readonly stemLibraryTrigger: string;
    readonly stemAllSubjects: string;
    readonly stemGenerateSectionTitle: string;
    readonly stemSidebarTitle: string;
    readonly stemSidebarCloseAria: string;
    readonly stemCategoryLabels: Readonly<
      Record<SubjectCategoryId, { readonly label: string; readonly description: string }>
    >;
    readonly latexTrigger: string;
    readonly simulationTrigger: string;
    readonly youtubeTrigger: string;
    readonly laserAria: string;
    readonly laserTitle: string;
    readonly cursorChatTriggerAria: string;
    readonly cursorChatPlaceholder: string;
    readonly cursorChatHint: string;
    readonly remoteHostBadge: string;
    readonly remoteOverflowBadge: (count: number) => string;
    readonly teacherPermissionsAria: string;
    readonly screenFollowYou: string;
    readonly screenFreeMove: string;
    readonly screenFollowingTeacher: string;
    readonly drawCustom: string;
    readonly drawViewOnly: string;
    readonly undoAria: string;
    readonly undoTitle: string;
    readonly redoAria: string;
    readonly redoTitle: string;
    readonly zoomOutAria: string;
    readonly zoomInAria: string;
    readonly zoomMenuAria: string;
    readonly zoomMenuHeader: (percent: number) => string;
    readonly fitSlide: string;
    readonly fitWidth: string;
    readonly zoom100Label: string;
    readonly pageNumberInputAria: string;
  };
  readonly permissions: {
    readonly teacherDialogTitle: string;
    readonly teacherScreenSection: string;
    readonly followYouBtn: string;
    readonly freeMoveBtn: string;
    readonly teacherFollowHint: string;
    readonly teacherFreeHint: string;
    readonly drawingPermissionSection: string;
    readonly viewOnlyMode: string;
    readonly customMode: string;
    readonly viewOnlyHint: string;
    readonly customModeHint: string;
    readonly searchParticipantPlaceholder: string;
    readonly selectedCount: (editable: number, total: number) => string;
    readonly deselectAllBtn: string;
    readonly selectStudentAria: (name: string) => string;
  };
  readonly pinyinModal: {
    readonly title: string;
    readonly subtitle: string;
    readonly modeCardLabel: string;
    readonly modeCardDesc: string;
    readonly modeTextLabel: string;
    readonly modeTextDesc: string;
    readonly pinyinInputLabel: string;
    readonly pinyinInputPlaceholder: string;
    readonly convertedPreviewLabel: string;
    readonly hanziInputLabel: string;
    readonly hanziInputPlaceholder: string;
    readonly gridStyleLabel: string;
    readonly gridTianzige: string;
    readonly gridMizige: string;
    readonly toneVowelsTitle: string;
    readonly punctuationTitle: string;
    readonly colorLabel: string;
    readonly strokeColorLabels: {
      readonly ink: string;
      readonly white: string;
      readonly crimson: string;
      readonly blue: string;
      readonly emerald: string;
      readonly amber: string;
    };
    readonly copyPinyinButton: string;
    readonly copiedPinyinButton: string;
    readonly cancelButton: string;
    readonly insertCardButton: string;
    readonly insertTextButton: string;
    readonly practiceTitle: string;
    readonly practiceFull: string;
    readonly practiceTrace: string;
    readonly practiceDictation: string;
    readonly practicePinyinQuiz: string;
    readonly gridJiugongge: string;
    readonly gridSikexian: string;
    readonly presetsTitle: string;
    readonly cellsCountBadge: (count: number) => string;
  };
  readonly modals: {
    readonly cancel: string;
    readonly close: string;
    readonly confirm: string;
    readonly latexEditTitle: string;
    readonly latexCloseTitle: string;
    readonly latexChipInsertTitle: (latex: string) => string;
    readonly latexChips: Readonly<Record<LatexChipId, string>>;
    readonly latexInkColors: Readonly<Record<LatexColorId, string>>;
    readonly latexInputPlaceholder: string;
    readonly latexSyntaxWarning: string;
    readonly latexEmptyPreviewHint: string;
    readonly latexInkColorLabel: string;
    readonly latexInsertingButton: string;
    readonly latexUpdateButton: string;
    readonly latexInsertButton: string;
    readonly simulationTitle: string;
    readonly simulationSubtitle: string;
    readonly simulationInsertButton: string;
    readonly studios: {
      readonly graphing: StudioCardTranslation;
      readonly geometry: StudioCardTranslation;
    };
    readonly youtubeTitle: string;
    readonly youtubeSubtitle: string;
    readonly youtubeUrlLabel: string;
    readonly youtubeUrlPlaceholder: string;
    readonly youtubeInvalidUrlError: string;
    readonly youtubeDisplayTitleLabel: string;
    readonly youtubePreviewLabel: string;
    readonly youtubeStartTimeBadge: (seconds: number) => string;
    readonly youtubeInsertButton: string;
    readonly videoUploadTitle: string;
    readonly videoUnsupportedError: string;
    readonly videoPresignFailedError: (status: number) => string;
    readonly videoUploadHttpFailedError: (status: number) => string;
    readonly videoUploadNetworkError: string;
    readonly mermaidTitle: string;
    readonly mermaidSubtitle: string;
    readonly mermaidCloseAria: string;
    readonly mermaidTemplatesLabel: string;
    readonly mermaidTemplateFlowchart: string;
    readonly mermaidTemplateSequence: string;
    readonly mermaidTemplateClass: string;
    readonly mermaidSyntaxLabel: string;
    readonly mermaidKeyboardHint: string;
    readonly mermaidEmptyError: string;
    readonly mermaidNoElementsError: string;
    readonly mermaidSyntaxError: (error: string) => string;
    readonly mermaidSupportedDiagramsHint: string;
    readonly mermaidCancelButton: string;
    readonly mermaidProcessingButton: string;
    readonly mermaidInsertButton: string;
    readonly webEmbedTitle: string;
    readonly webEmbedSubtitle: string;
    readonly webEmbedCloseAria: string;
    readonly webEmbedUrlLabel: string;
    readonly webEmbedUrlPlaceholder: string;
    readonly webEmbedNameLabel: string;
    readonly webEmbedNamePlaceholder: string;
    readonly webEmbedInvalidUrlError: string;
    readonly webEmbedCancelButton: string;
    readonly webEmbedInsertButton: string;
    readonly mathDockPlotToGraphTitle: string;
    readonly mathDockPlotToGraphButton: string;
    readonly mathDockStampToBoardTitle: string;
    readonly mathDockStampToBoardButton: string;
    readonly mathDockCollapseTitle: string;
    readonly mathDockExpandTitle: string;
    readonly mathDockCollapseButton: string;
    readonly mathDockExpandButton: string;
    readonly mathDockDoneButton: string;
    readonly mathDockTabAlgebra: string;
    readonly mathDockTabFunctions: string;
    readonly mathDockTabPresets: string;
    readonly mathDockTabCalculus: string;
    readonly mathDockTemplates: Readonly<Record<string, string>>;
  };
  readonly widgets: {
    readonly modeToggleToAnnotateTitle: string;
    readonly modeToggleToInteractTitle: string;
    readonly interactLabel: string;
    readonly annotateLabel: string;
    readonly unlockWidgetTitle: string;
    readonly lockWidgetTitle: string;
    readonly lockedByTeacherTitle: string;
    readonly maximizeWidgetTitle: string;
    readonly restoreWidgetTitle: string;
    readonly confirmDeleteWidgetTitle: string;
    readonly confirmDeleteWidgetLabel: string;
    readonly cancelDeleteWidgetTitle: string;
    readonly cancelDeleteWidgetLabel: string;
    readonly deleteWidgetAria: string;
    readonly resizeHandleTitle: string;
    readonly microPromptTouchPoint: string;
    readonly microPromptPassThroughPoint: string;
    readonly microPromptPerpendicularEdge: string;
    readonly microPromptParallelEdge: string;
    readonly microPromptCancelTitle: string;
    readonly openSlidersTitle: string;
    readonly parametersLabel: string;
    readonly collapseSlidersTitle: string;
    readonly collapseLabel: string;
    readonly videoIndependentTitle: string;
    readonly videoFollowingTitle: string;
    readonly videoIndependentLabel: string;
    readonly videoFollowTeacherLabel: string;
    readonly videoResyncButton: string;
    readonly videoResyncTitle: string;
    readonly hidePlotTitle: string;
    readonly showPlotTitle: string;
    readonly hideInequalityTitle: string;
    readonly showInequalityTitle: string;
    readonly hideConicTitle: string;
    readonly showConicTitle: string;
    readonly inequalityShortTag: string;
    readonly deleteExpressionTitle: (label: string) => string;
    readonly invalidSyntaxLabel: string;
    readonly mobileExpressionListTitle: string;
    readonly addButtonLabel: string;
    readonly openMobileFormulaSheetTitle: string;
    readonly emptyExpressionLabel: string;
    readonly openAlgebraDrawerTitle: string;
    readonly expressionListTitle: string;
    readonly collapseAlgebraDrawerTitle: string;
    readonly omniInputPlaceholder: string;
    readonly omniInputAria: string;
    readonly clickToAddExpression: string;
    readonly newBadge: string;
    readonly addPointLabel: string;
    readonly triangleAbcLabel: string;
    readonly triangleLabel: string;
    readonly segmentLabel: string;
    readonly lineLabel: string;
    readonly vectorLabel: string;
    readonly perpendicularLabel: string;
    readonly parallelLabel: string;
    readonly circleLabel: string;
    readonly polygonLabel: string;
    readonly clearGeometryBoardTitle: string;
    readonly geometryPresets: Readonly<Record<GeometryPresetKey, string>>;
    readonly extremumMaxBadge: (x: string, y: string) => string;
    readonly extremumMinBadge: (x: string, y: string) => string;
    readonly rootSweeperBadge: (paramName: string, val: string, count: number) => string;
    readonly lpUnboundedReasons: {
      readonly both_unbounded: string;
      readonly max_unbounded: string;
      readonly min_unbounded: string;
    };
    readonly lpTableHeader: (fnExpr: string) => string;
    readonly lpVertexCol: string;
    readonly lpCoordsCol: string;
    readonly lpMaxSummary: (val: string, label: string, x: string, y: string) => string;
    readonly lpMinSummary: (val: string, label: string, x: string, y: string) => string;
    readonly stopwatchPauseTitle: string;
    readonly stopwatchPlayTitle: string;
    readonly stopwatchResetTitle: string;
    readonly riemannMethodLeft: string;
    readonly riemannMethodMidpoint: string;
    readonly riemannMethodRight: string;
    readonly riemannMethodTrapezoidal: string;
    readonly riemannExpandTitle: string;
    readonly riemannCollapseTitle: string;
    readonly riemannCloseTitle: string;
    readonly riemannFullTitle: string;
    readonly riemannDec5Title: string;
    readonly riemannDec1Title: string;
    readonly riemannInputTitle: string;
    readonly riemannInc1Title: string;
    readonly riemannInc5Title: string;
    readonly sweeperExpandTitle: string;
    readonly sweeperCollapseTitle: string;
    readonly sweeperCloseTitle: string;
    readonly sweeperFullTitle: string;
    readonly sweeperInputTitle: string;
    readonly sweeperDec05Title: string;
    readonly sweeperDec01Title: string;
    readonly sweeperResetZeroTitle: string;
    readonly sweeperInc01Title: string;
    readonly sweeperInc05Title: string;
    readonly insituCircumcircleLabel: string;
    readonly insituIncircleLabel: string;
    readonly insituAltitudesLabel: string;
    readonly insituMediansLabel: string;
    readonly insituBisectorsLabel: string;
    readonly insituMeasurementsLabel: string;
    readonly insituMidpointLabel: string;
    readonly insituBisectorLabel: string;
    readonly insituLengthLabel: string;
    readonly insituCircleHeader: string;
    readonly insituTwoTangentsLabel: (ptId: string) => string;
    readonly insituCreateTangentsLabel: string;
    readonly insituPointLabelPrefix: string;
    readonly insituVertexAltitudeTitle: string;
    readonly insituVertexMedianTitle: string;
    readonly insituVertexBisectorTitle: string;
    readonly insituPolygonHeader: (count: number) => string;
    readonly insituDeleteElementTitle: string;
    readonly insituCloseTitle: string;
    readonly dockCompactTriggerTitle: string;
    readonly dockCompactTriggerLabel: string;
    readonly dockMenuHeader: string;
    readonly dockMoreActionsTitle: string;
    readonly dockMoreActionsLabel: (count: number) => string;
    readonly dockAllActionsHeader: string;
    readonly tangentOutOfDomainBadge: (x0: string) => string;
    readonly unmuteVideoLabel: string;
    readonly snapIsoscelesBadge: string;
    readonly emptyFeasibleRegionNotice: string;
    readonly feasiblePointMemberBadge: (objValueStr: string) => string;
    readonly feasiblePointViolatedBadge: (expr: string) => string;
    readonly pedagogicalActions: {
      readonly hatchingLabel: string;
      readonly hatchingTitle: string;
      readonly objectiveOptimizerLabel: string;
      readonly objectiveOptimizerTitle: string;
      readonly curveIntersectionsLabel: (plotName: string, count: number) => string;
      readonly curveIntersectionsTitle: (plotName: string) => string;
      readonly areaBetweenCurvesLabel: string;
      readonly areaBetweenCurvesTitle: string;
      readonly conicAxesLabel: (isEllipse: boolean) => string;
      readonly conicAxesTitle: string;
      readonly conicFociLabel: string;
      readonly conicFociTitle: string;
      readonly asymptotesLabel: string;
      readonly asymptotesTitle: string;
      readonly symmetryCenterLabel: string;
      readonly symmetryCenterTitle: string;
      readonly quadraticVertexAxisLabel: string;
      readonly quadraticVertexAxisTitle: string;
      readonly monotonicityLabel: string;
      readonly monotonicityTitle: string;
      readonly inflectionPointLabel: string;
      readonly inflectionPointTitle: string;
      readonly keyFeaturesLabel: string;
      readonly keyFeaturesTitle: string;
      readonly curveProbeLabel: string;
      readonly curveProbeTitle: string;
      readonly intervalAnalysisLabel: string;
      readonly intervalAnalysisTitle: string;
      readonly rootSweeperLabel: string;
      readonly rootSweeperTitle: string;
      readonly tangentLineLabel: string;
      readonly tangentLineTitle: string;
      readonly riemannSumLabel: string;
      readonly riemannSumTitle: string;
      readonly definiteIntegralLabel: string;
      readonly definiteIntegralTitle: string;
      readonly absFTransformLabel: string;
      readonly absFTransformTitle: string;
      readonly fAbsTransformLabel: string;
      readonly fAbsTransformTitle: string;
    };
  };
}

const TRANSLATION_MAP: Readonly<Record<LocaleCode, WhiteboardTranslations>> = {
  vi: VI_TRANSLATIONS,
  "zh-CN": ZH_CN_TRANSLATIONS,
  "zh-TW": ZH_TW_TRANSLATIONS,
  en: EN_TRANSLATIONS,
};

export function getTranslations(locale: LocaleCode): WhiteboardTranslations {
  return TRANSLATION_MAP[locale];
}
